import React, { useState, useEffect } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { API_BASE } from "../config";

export default function EnergyClubDashboard() {
  const [energyData, setEnergyData] = useState([]);
  const [pieData, setPieData] = useState([]);

  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  // -----------------------------------------
  // UI SELECTED FILTERS (Dropdown controlled)
  // -----------------------------------------
  const [selectedDistrict, setSelectedDistrict] = useState("0");
  const [selectedActivity, setSelectedActivity] = useState("0");

  // FY selector
  const getCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month < 4 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  };

  const currentFY = getCurrentFY();
  const [selectedFY, setSelectedFY] = useState(currentFY);

  const getFYList = () => {
    const [start, end] = currentFY.split("-").map(Number);
    return [
      `${start - 1}-${end - 1}`,
      `${start}-${end}`,
      `${start + 1}-${end + 1}`,
    ];
  };
  const financialYears = getFYList();

  // --------------------------------------------------------
  // 🆕 APPLIED FILTERS (Used for Heading + API query)
  // --------------------------------------------------------
  const [appliedDistrict, setAppliedDistrict] = useState("0"); // 🆕 ADDED
  const [appliedActivity, setAppliedActivity] = useState("0"); // 🆕 ADDED
  const [appliedFY, setAppliedFY] = useState(currentFY);       // 🆕 ADDED

  // FY to date range
  const getFYDates = (fy) => {
    const [startYear, endYear] = fy.split("-");
    return {
      fy_start: `${startYear}-04-01`,
      fy_end: `${endYear}-03-31`,
    };
  };

  // -----------------------------------
  // LOAD MASTER DATA
  // -----------------------------------
  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((res) => setDistricts(res.data));
    axios.get(`${API_BASE}/activities`).then((res) => setActivities(res.data));
  }, []);

  // -----------------------------------------------
  // 🚀 CHANGED — fetchEnergyClub NOW USES PARAMETERS
  // -----------------------------------------------
  const fetchEnergyClub = async (
    dist = appliedDistrict,
    activity = appliedActivity,
    fy = appliedFY
  ) => {
    try {
      const { fy_start, fy_end } = getFYDates(fy);

      const res = await axios.get(`${API_BASE}/energy-club-filter`, {
        params: { dist, activity, fy_start, fy_end },
      });

      const barChartData = res.data.map((row) => ({
        name: row.district_name,
        district_id: row.dist_id,
        count: Number(row.club_count),
      }));

      const pieChartData = res.data.map((row) => ({
        id: row.dist_id,
        label: row.district_name,
        value: Number(row.club_count),
        district_id: row.dist_id,
      }));

      setEnergyData(barChartData);
      setPieData(pieChartData);
    } catch (err) {
      console.error("Error fetching Energy Club data:", err);
      setEnergyData([]);
      setPieData([]);
    }
  };

  useEffect(() => {
    fetchEnergyClub(); // initial load
  }, []);

  // -----------------------------------------------
  // 🆕 DYNAMIC HEADING VALUES BASED ON APPLIED FILTERS
  // -----------------------------------------------
  const appliedDistrictName =
    appliedDistrict === "0"
      ? "All"
      : districts.find((d) => String(d.dist_id) === appliedDistrict)?.district ||
        "All";

  const appliedActivityName =
    appliedActivity === "0"
      ? "All"
      : activities.find(
          (a) => String(a.activities_id) === appliedActivity
        )?.activity || "All";

  const appliedFYLabel = appliedFY || "All";

  const barChartSettings = {
    keys: ["count"],
    indexBy: "name",
    margin: { top: 80, right: 50, bottom: 160, left: 70 }, // increased
    padding: 0.35,
    colors: (bar) => ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"][bar.index % 4],
    borderColor: { from: "color", modifiers: [["darker", 1.2]] },
  
    axisBottom: {
      tickRotation: -35,
      tickSize: 5,
      tickPadding: 15, // increased
      legend: "District Names",
      legendPosition: "middle",
      legendOffset: 100, // moved further down
    },
  
    axisLeft: {
      legend: "All Activity Submission Status",
      legendPosition: "middle",
      legendOffset: -50,
    },
  
    borderRadius: 6,
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    labelTextColor: "#000",
  
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom",
        direction: "row",
        translateY: 80,
        itemsSpacing: 12,
        itemWidth: 140,
        itemHeight: 20,
        symbolSize: 18,
      },
    ],
  };
  

  const pieChartSettings = {
    margin: { top: 40, right: 20, bottom: 40, left: 20 }, // NEW
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 4,
    activeOuterRadiusOffset: 6,
    colors: { scheme: "pastel1" },
  };



  const chartHeading = (title) => (
    <span>
      {title} (
        District: <span style={{ color: "green", fontWeight: "600" }}>{appliedDistrictName}
      </span>{" "}
      |{" "}
      
        Activity: <span style={{ color: "green", fontWeight: "600" }}>{appliedActivityName}
      </span>{" "}
      |{" "}
      
        FY: <span style={{ color: "green", fontWeight: "600" }}>{appliedFYLabel}
      </span>
      )
    </span>
  );
  

  // -----------------------------------------------
  // COMPONENT UI
  // -----------------------------------------------
  return (
    <div style={{ padding: "30px", background: "#f4f6f8", minHeight: "100vh" }}>
      <h2 style={{ textAlign: "left",marginTop: "0" }}>Activity Submission Status</h2>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
        
        {/* District */}
        <label>
          District:
          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
            style={{ marginLeft: 10, padding: 5 }}
          >
            <option value="0">-- All --</option>
            {districts.map((d) => (
              <option key={d.dist_id} value={d.dist_id}>
                {d.district}
              </option>
            ))}
          </select>
        </label>

        {/* Activity */}
        <label>
          Activity:
          <select
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
            style={{ marginLeft: 10, padding: 5 }}
          >
            <option value="0">-- All --</option>
            {activities.map((a) => (
              <option key={a.activities_id} value={a.activities_id}>
                {a.activity}
              </option>
            ))}
          </select>
        </label>

        {/* FY */}
        <label>
          Financial Year:
          <select
            value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}
            style={{ marginLeft: 10, padding: 5 }}
          >
            {financialYears.map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </label>

        {/* SEARCH BUTTON */}
        <button
          style={{
            padding: "10px 25px",
            background: "#2196F3",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            marginTop: "-3px",
          }}
          onClick={() => {
            // 🆕 APPLY FILTERS HERE (Heading + API should reflect these)
            setAppliedDistrict(selectedDistrict);
            setAppliedActivity(selectedActivity);
            setAppliedFY(selectedFY);

            // 🚀 NOW FETCH USING NEW APPLIED FILTERS
            fetchEnergyClub(selectedDistrict, selectedActivity, selectedFY);
          }}
        >
          Search
        </button>
      </div>

      {/* BAR CHART */}
      <div style={{ height: 450, background: "#fff", padding: 20, borderRadius: 12, marginBottom: 35 }}>
        
        {/* 🆕 DYNAMIC HEADING */}
        <h3>{chartHeading("Bar Chart: District-wise Activity Submission Status")}</h3>

     <ResponsiveBar
  data={energyData}
  {...barChartSettings}

  tooltip={({ data }) => (
    <div
      style={{
        padding: "6px 10px",
        background: "#fff",
        borderRadius: "6px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      }}
    >
      <strong>{data.name}</strong>: {data.count}
    </div>
  )}

  isInteractive={true}
  onMouseEnter={() => (document.body.style.cursor = "pointer")}
  onMouseLeave={() => (document.body.style.cursor = "default")}

  keys={["count"]}
  indexBy="name"
  colors={(bar) => ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"][bar.index % 4]}

  onClick={(bar) => {
    sessionStorage.setItem("viewSchoolsFilters", JSON.stringify({
      district: bar.data.district_id,
      fy: appliedFY,
      activity: appliedActivity,
      status: "submitted",
    }));
    window.open("/view-schools", "_blank");
  }}
/>

      </div>

      {/* PIE CHART */}
      <div style={{ height: 450, background: "#fff", padding: "20px", borderRadius: 12 }}>
        
        {/* 🆕 DYNAMIC HEADING */}
        <h3 style={{ marginBottom: 15 }}>
          {chartHeading("Pie Chart: District-wise Activity Submission Status")}
        </h3>

        <ResponsivePie
          data={pieData}
          {...pieChartSettings}

          theme={{
            background: "transparent",
            labels: { text: { cursor: "pointer" } },
        
            // ⭐ This makes bars show pointer cursor
            crosshair: { line: { stroke: "transparent" } },
            tooltip: { container: { cursor: "pointer" } },
          }}
        
          // ⭐ Add this so hover is enabled
          isInteractive={true}
        
          onMouseEnter={() => {
            document.body.style.cursor = "pointer"; // ⭐ CHANGED
          }}
          onMouseLeave={() => {
            document.body.style.cursor = "default"; // ⭐ CHANGED
          }}
          innerRadius={0.5}

          tooltip={({ datum }) => (
            <div
              style={{
                padding: "6px 10px",
                background: "#fff",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              <strong>{datum.label}</strong> : {datum.value}
            </div>
          )}

          arcLinkLabel={(d) => d.label}
          // onClick={(slice) => {
          //   window.open(`/view-schools?district=${slice.data.district_id}&fy=${appliedFY}&activity=${appliedActivity}`, "_blank");
          // }}
          onClick={(slice) => {
            sessionStorage.setItem("viewSchoolsFilters", JSON.stringify({
              district: slice.data.district_id,
              fy: appliedFY,
              activity: appliedActivity,
              status: "submitted",
            }));
          
            window.open("/view-schools", "_blank");
          }}
        />
      </div>
    </div>
  );
}
